import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Paper,
} from '@mui/material';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import * as ETVConstants from '../ETVConstants';

const DAYS = [
  { key: 'mon', label: ETVConstants.trans('monday') },
  { key: 'tue', label: ETVConstants.trans('tuesday') },
  { key: 'wed', label: ETVConstants.trans('wednesday') },
  { key: 'thu', label: ETVConstants.trans('thursday') },
  { key: 'fri', label: ETVConstants.trans('friday') },
  { key: 'sat', label: ETVConstants.trans('saturday') },
  { key: 'sun', label: ETVConstants.trans('sunday') },
];

// Canonical full-day marker in your system
const FULL_DAY_SLOT = { start: '00:00', end: '00:00' };
const isFullDaySlot = (r) => r?.start === '00:00' && r?.end === '00:00';

const emptyWeekly = () => Object.fromEntries(DAYS.map((d) => [d.key, []]));

const toMinutes = (hhmm) => {
  if (hhmm == null) return null;
  const s = String(hhmm).trim();
  const [hhStr, mmStr] = s.split(':');
  if (hhStr == null || mmStr == null) return null;
  const hh = parseInt(hhStr, 10);
  const mm = parseInt(mmStr, 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  // allow 24:00 as a special case if it ever appears
  if (hh < 0 || hh > 24) return null;
  if (mm < 0 || mm > 59) return null;
  if (hh === 24 && mm !== 0) return null;

  return hh * 60 + mm; // 24:00 => 1440
};

const isHHMM = (s) => typeof s === 'string' && /^\d{2}:\d{2}$/.test(s) && toMinutes(s) != null;

// treat "...–00:00" as end-of-day (24:00), but keep 00:00–00:00 as FULL_DAY
const endMinutes = (startHHMM, endHHMM) => {
  const s = toMinutes(startHHMM);
  const e = toMinutes(endHHMM);
  if (s == null || e == null) return null;

  if (String(endHHMM).trim() === '00:00' && String(startHHMM).trim() !== '00:00') return 1440;
  return e;
};

const isEndOfDayEnd = (endHHMM) => String(endHHMM).trim() === '00:00';

const clampEndSameDay = (startDj, endDj) => {
  const nextMidnight = startDj.startOf('day').add(1, 'day');
  return endDj.isAfter(nextMidnight) ? nextMidnight : endDj;
};

// If any full-day slot exists, collapse to exactly [FULL_DAY_SLOT]
const canonicalizeSlots = (arr) => {
  const a = Array.isArray(arr) ? arr : [];
  if (a.some(isFullDaySlot)) return [FULL_DAY_SLOT];
  return a;
};

const ensureWeeklyShape = (w) => {
  const base = emptyWeekly();
  if (!w || typeof w !== 'object') return base;

  for (const d of DAYS) {
    if (!Array.isArray(w[d.key])) {
      base[d.key] = [];
    } else {
      base[d.key] = canonicalizeSlots(
        w[d.key]
          .filter((x) => x && isHHMM(x.start) && isHHMM(x.end))
          .map((x) => ({ start: String(x.start).trim(), end: String(x.end).trim() }))
      );
    }
  }
  return base;
};

function normalizeWeekly(value) {
  if (!value || typeof value !== 'object') return emptyWeekly();
  const src = value.weekly && typeof value.weekly === 'object' ? value.weekly : value;
  return ensureWeeklyShape(src);
}

function normalizeDaily(value) {
  const arr = value && Array.isArray(value.daily) ? value.daily : [];
  return canonicalizeSlots(
    arr
      .filter((x) => x && isHHMM(x.start) && isHHMM(x.end))
      .map((x) => ({ start: String(x.start).trim(), end: String(x.end).trim() }))
  );
}

function initialMode(value) {
  if (value && (value.mode === 'daily' || value.mode === 'weekly')) return value.mode;
  return 'daily';
}

const parseHHMM = (s) => {
  if (!s) return null;
  const [hh, mm] = String(s).split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  // 24:00 can't be represented by dayjs time picker; map it to 00:00 display
  if (hh === 24 && mm === 0) return dayjs().hour(0).minute(0).second(0).millisecond(0);
  return dayjs().hour(hh).minute(mm).second(0).millisecond(0);
};

const formatHHMM = (d) => (d && dayjs.isDayjs(d) ? d.format('HH:mm') : '');

const hasOverlap = (ranges) => {
  const arr = canonicalizeSlots(ranges || [])
    .filter((r) => r && typeof r.start === 'string' && typeof r.end === 'string')
    .map((r) => {
      if (isFullDaySlot(r)) return { startMin: 0, endMin: 1440, fullDay: true };

      const s = toMinutes(r.start);
      const e = endMinutes(r.start, r.end);
      if (s == null || e == null) return null;
      if (e <= s) return null;

      return { startMin: s, endMin: e, fullDay: false };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMin - b.startMin);

  // after canonicalizeSlots, this is mostly redundant, but keep safe:
  const fullDayCount = arr.filter((x) => x.fullDay).length;
  if (fullDayCount >= 1 && arr.length > 1) return true;

  for (let i = 1; i < arr.length; i++) {
    if (arr[i - 1].startMin < arr[i].endMin && arr[i].startMin < arr[i - 1].endMin) return true;
  }
  return false;
};

function expandDailyToWeekly(daily) {
  const w = emptyWeekly();
  const d = canonicalizeSlots(daily || []);
  for (const day of DAYS) w[day.key] = d.map((x) => ({ ...x }));
  return w;
}

function deriveDailyFromWeekly(weekly) {
  const w = ensureWeeklyShape(weekly);
  const first = JSON.stringify(w[DAYS[0].key] || []);
  for (const d of DAYS.slice(1)) {
    if (JSON.stringify(w[d.key] || []) !== first) return [];
  }
  return canonicalizeSlots((w[DAYS[0].key] || []).map((x) => ({ ...x })));
}

// NEW RULE: apply default ONLY if value is actually null/undefined (not {}, not {daily:[],weekly:{}})
const shouldApplyDefault = (value) => value == null;

const signature = (v) => {
  try {
    return JSON.stringify({
      mode: initialMode(v),
      weekly: normalizeWeekly(v),
      daily: normalizeDaily(v),
    });
  } catch {
    return '';
  }
};

export default class Schedule extends React.Component {
  static defaultProps = {
    defaultDailySlots: [FULL_DAY_SLOT], // only used when props.value == null
    title: 'Schedule',
    onSchedule: () => {},
  };

  constructor(props) {
    super(props);

    const noValueProvided = shouldApplyDefault(props.value);

    let d = normalizeDaily(props.value);
    if (noValueProvided && d.length === 0) {
      d = (props.defaultDailySlots && props.defaultDailySlots.length)
        ? canonicalizeSlots(props.defaultDailySlots)
        : [FULL_DAY_SLOT];
    }

    this.state = {
      mode: initialMode(props.value),
      weekly: noValueProvided ? expandDailyToWeekly(d) : normalizeWeekly(props.value),
      daily: d,
      _propSig: signature(props.value),
    };
  }

  componentDidUpdate(prevProps) {
    if (signature(prevProps.value) === signature(this.props.value)) return;

    const noValueProvided = shouldApplyDefault(this.props.value);

    let d = normalizeDaily(this.props.value);
    if (noValueProvided && d.length === 0) {
      d = (this.props.defaultDailySlots && this.props.defaultDailySlots.length)
        ? canonicalizeSlots(this.props.defaultDailySlots)
        : [FULL_DAY_SLOT];
    }

    this.setState({
      mode: initialMode(this.props.value),
      weekly: noValueProvided ? expandDailyToWeekly(d) : normalizeWeekly(this.props.value),
      daily: d,
      _propSig: signature(this.props.value),
    });
  }

  // ------------------ Emit to parent only ------------------
  emit = (
    nextMode = this.state.mode,
    nextWeekly = this.state.weekly,
    nextDaily = this.state.daily
  ) => {
    const safeWeekly = ensureWeeklyShape(nextWeekly);
    const safeDaily = canonicalizeSlots(nextDaily || []);
    const next = { mode: nextMode, weekly: safeWeekly, daily: safeDaily };

    this.setState(next, () => {
      if (this.props.onSchedule) this.props.onSchedule(next);
      if (this.props.onChange) this.props.onChange(next);
    });
  };

  // ------------------ Weekly ------------------
  updateDay = (dayKey, nextRanges) => {
    const safeRanges = canonicalizeSlots(nextRanges || []);
    const nextW = ensureWeeklyShape({ ...this.state.weekly, [dayKey]: safeRanges });
    this.emit('weekly', nextW, deriveDailyFromWeekly(nextW));
  };

  addWeeklySlot = (dayKey) => {
    let current = canonicalizeSlots((this.state.weekly[dayKey] || []).slice());

    // if full-day exists, keep it (one-click add just stays full-day)
    current = current.filter((r) => !isFullDaySlot(r));
    if (current.length === 0) {
      this.updateDay(dayKey, [FULL_DAY_SLOT]);
      return;
    }

    const last = current[current.length - 1];
    if (isEndOfDayEnd(last.end)) return;

    const start = parseHHMM(last.end) || parseHHMM('00:00');
    let end = start.add(180, 'minute');
    end = clampEndSameDay(start, end);

    const endStr = end.isSame(start.startOf('day').add(1, 'day')) ? '00:00' : formatHHMM(end);
    this.updateDay(dayKey, [...current, { start: formatHHMM(start), end: endStr }]);
  };

  removeWeeklySlot = (dayKey, idx) => {
    const next = (this.state.weekly[dayKey] || []).filter((_, i) => i !== idx);
    this.updateDay(dayKey, next);
  };

  setWeeklyTime = (dayKey, idx, field, dj) => {
    if (!dj) return;
    const next = (this.state.weekly[dayKey] || []).map((r, i) =>
      i === idx ? { ...r, [field]: formatHHMM(dj) } : r
    );
    this.updateDay(dayKey, next);
  };

  // ------------------ Daily ------------------
  updateDaily = (nextRanges) => {
    const safe = canonicalizeSlots(nextRanges || []);
    this.emit('daily', expandDailyToWeekly(safe), safe);
  };

  addDailySlot = () => {
    let current = canonicalizeSlots((this.state.daily || []).slice());
    current = current.filter((r) => !isFullDaySlot(r));

    if (current.length === 0) {
      this.updateDaily([FULL_DAY_SLOT]);
      return;
    }

    const last = current[current.length - 1];
    if (isEndOfDayEnd(last.end)) return;

    const start = parseHHMM(last.end) || parseHHMM('00:00');
    let end = start.add(180, 'minute');
    end = clampEndSameDay(start, end);

    const endStr = end.isSame(start.startOf('day').add(1, 'day')) ? '00:00' : formatHHMM(end);
    this.updateDaily([...current, { start: formatHHMM(start), end: endStr }]);
  };

  removeDailySlot = (idx) => {
    const next = (this.state.daily || []).filter((_, i) => i !== idx);
    this.updateDaily(next);
  };

  setDailyTime = (idx, field, dj) => {
    if (!dj) return;
    const next = (this.state.daily || []).map((r, i) =>
      i === idx ? { ...r, [field]: formatHHMM(dj) } : r
    );
    this.updateDaily(next);
  };

  // ------------------ Presets ------------------
  applyPresetAllDays = (slots) => {
    const safeSlots = canonicalizeSlots(slots || []);
    const next = emptyWeekly();
    for (const d of DAYS) next[d.key] = safeSlots.map((s) => ({ ...s }));
    this.emit('weekly', next, deriveDailyFromWeekly(next));
  };

  applyPresetWeekdays = (slots) => {
    const safeSlots = canonicalizeSlots(slots || []);
    const next = ensureWeeklyShape(this.state.weekly);
    for (const d of DAYS.slice(0, 5)) next[d.key] = safeSlots.map((s) => ({ ...s }));
    this.emit('weekly', next, deriveDailyFromWeekly(next));
  };

  // ------------------ Render ------------------
  render() {
    const { title } = this.props;
    const { mode, weekly, daily } = this.state;

    const dailyAddDisabled = (() => {
      const current = (daily || []).filter((r) => !isFullDaySlot(r));
      if (current.length === 0) return false;
      return current[current.length - 1]?.end === '00:00';
    })();

    const dailyInvalid =
      hasOverlap(daily) ||
      (daily || []).some((r) => {
        if (isFullDaySlot(r)) return false;
        const s = toMinutes(r.start);
        const e = endMinutes(r.start, r.end);
        return !(s != null && e != null && e > s);
      });

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <ScheduleIcon />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Chip
                size="small"
                label={mode === 'daily' ? ETVConstants.trans('daily_mode') : ETVConstants.trans('weekly_mode')}
                variant="filled"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'primary.contrastText' }}
              />
            </Box>

            <ToggleButtonGroup
              size="small"
              value={mode}
              exclusive
              color="standard"
              onChange={(_, v) => {
                if (!v) return;

                if (v === 'daily') {
                  const nextDaily = this.state.daily.length ? this.state.daily : deriveDailyFromWeekly(this.state.weekly);
                  this.emit('daily', expandDailyToWeekly(nextDaily), nextDaily);
                } else {
                  const wHasData = Object.values(this.state.weekly || {}).some(
                    (arr) => Array.isArray(arr) && arr.length > 0
                  );
                  const nextWeekly = wHasData ? this.state.weekly : expandDailyToWeekly(this.state.daily);
                  this.emit('weekly', nextWeekly, deriveDailyFromWeekly(nextWeekly));
                }
              }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 3,
                '& .MuiToggleButton-root': { color: 'primary.contrastText', border: 0 },
                '& .Mui-selected': { bgcolor: 'rgba(255,255,255,0.25) !important' },
              }}
            >
              <ToggleButton value="daily" aria-label="Daily">
                <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} /> {ETVConstants.trans('daily')}
              </ToggleButton>
              <ToggleButton value="weekly" aria-label="Weekly">
                <CalendarViewWeekIcon fontSize="small" sx={{ mr: 1 }} /> {ETVConstants.trans('weekly')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <CardContent>
            {mode === 'daily' ? (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <strong>{ETVConstants.trans('daily_mode')}</strong>.&nbsp;{ETVConstants.trans('daily_mode_desc')}.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <strong>{ETVConstants.trans('weekly_mode')}.</strong> {ETVConstants.trans('weekly_mode_desc')}
              </Alert>
            )}

            {mode === 'daily' ? (
              // DAILY UI
              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {ETVConstants.trans('daily')}
                  </Typography>
                  <Button
                    type="button"
                    variant="contained"
                    size="small"
                    disabled={dailyAddDisabled}
                    startIcon={<AddIcon />}
                    onClick={this.addDailySlot}
                  >
                    {ETVConstants.trans('add_slot')}
                  </Button>
                </Box>

                <Box sx={{ p: 2 }}>
                  {this.state.daily.length === 0 ? (
                    <Box sx={{ py: 2, color: 'text.secondary' }}>
                      <Typography variant="body2">{ETVConstants.trans('no_slots_yet')}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Table size="small" sx={{ '& td, & th': { border: 0 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell width="45%">{ETVConstants.trans('start')}</TableCell>
                            <TableCell width="45%">{ETVConstants.trans('end')}</TableCell>
                            <TableCell align="right" width="10%"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.daily.map((r, idx) => {
                            const startDJ = parseHHMM(r.start) || dayjs().hour(0).minute(0);
                            const endDJ = parseHHMM(r.end) || dayjs().hour(0).minute(0);

                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <TimePicker
                                    value={startDJ}
                                    onChange={(v) => this.setDailyTime(idx, 'start', v)}
                                    ampm={false}
                                    views={['hours', 'minutes']}
                                    format="HH:mm"
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TimePicker
                                    value={endDJ}
                                    onChange={(v) => this.setDailyTime(idx, 'end', v)}
                                    ampm={false}
                                    views={['hours', 'minutes']}
                                    format="HH:mm"
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    type="button"
                                    aria-label="remove"
                                    color="error"
                                    onClick={() => this.removeDailySlot(idx)}
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>

                      {dailyInvalid && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'warning.main' }}>
                          <WarningAmberIcon fontSize="small" />
                          <Typography variant="caption">{ETVConstants.trans('check_times_overlap')}</Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            ) : (
              // WEEKLY UI
              <Grid container spacing={2}>
                {DAYS.map((d) => {
                  const ranges = canonicalizeSlots(weekly[d.key] || []);
                  const invalidAny =
                    hasOverlap(ranges) ||
                    (ranges || []).some((r) => {
                      if (isFullDaySlot(r)) return false;
                      const s = toMinutes(r.start);
                      const e = endMinutes(r.start, r.end);
                      return !(s != null && e != null && e > s);
                    });

                  const weeklyAddDisabled = (() => {
                    const cur = (ranges || []).filter((r) => !isFullDaySlot(r));
                    if (cur.length === 0) return false;
                    return cur[cur.length - 1]?.end === '00:00';
                  })();

                  return (
                    <Grid item xs={12} md={6} key={d.key}>
                      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {d.label}
                          </Typography>
                          <Button
                            type="button"
                            variant="contained"
                            size="small"
                            disabled={weeklyAddDisabled}
                            startIcon={<AddIcon />}
                            onClick={() => this.addWeeklySlot(d.key)}
                          >
                            {ETVConstants.trans('add_slot')}
                          </Button>
                        </Box>

                        <Box sx={{ p: 2 }}>
                          {ranges.length === 0 ? (
                            <Box sx={{ py: 2, color: 'text.secondary' }}>
                              <Typography variant="body2">{ETVConstants.trans('no_slots_yet')}</Typography>
                            </Box>
                          ) : (
                            <>
                              <Table size="small" sx={{ '& td, & th': { border: 0 } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="45%">{ETVConstants.trans('start')}</TableCell>
                                    <TableCell width="45%">{ETVConstants.trans('end')}</TableCell>
                                    <TableCell align="right" width="10%"></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {ranges.map((r, idx) => {
                                    const startDJ = parseHHMM(r.start) || dayjs().hour(0).minute(0);
                                    const endDJ = parseHHMM(r.end) || dayjs().hour(0).minute(0);

                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <TimePicker
                                            value={startDJ}
                                            onChange={(v) => this.setWeeklyTime(d.key, idx, 'start', v)}
                                            ampm={false}
                                            views={['hours', 'minutes']}
                                            format="HH:mm"
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <TimePicker
                                            value={endDJ}
                                            onChange={(v) => this.setWeeklyTime(d.key, idx, 'end', v)}
                                            ampm={false}
                                            views={['hours', 'minutes']}
                                            format="HH:mm"
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <IconButton
                                            type="button"
                                            aria-label="remove"
                                            color="error"
                                            onClick={() => this.removeWeeklySlot(d.key, idx)}
                                            size="small"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>

                              {invalidAny && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'warning.main' }}>
                                  <WarningAmberIcon fontSize="small" />
                                  <Typography variant="caption">{ETVConstants.trans('check_times_overlap')}</Typography>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Bottom action bar (weekly presets) */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {mode === 'daily' ? (
                <>
                  <Tooltip title="set 09:00–18:00 for every day">
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => this.updateDaily([{ start: '09:00', end: '18:00' }])}
                    >
                      {ETVConstants.trans('all_days_9_18')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="clear daily schedule">
                    <Button type="button" variant="outlined" color="error" size="small" onClick={() => this.updateDaily([])}>
                      {ETVConstants.trans('clear_daily')}
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title={ETVConstants.trans('weekdays_9_12')}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => this.applyPresetWeekdays([{ start: '09:00', end: '12:00' }])}
                    >
                      {ETVConstants.trans('weekdays_9_12')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="apply 9–18 to all days">
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => this.applyPresetAllDays([{ start: '09:00', end: '18:00' }])}
                    >
                      {ETVConstants.trans('all_days_9_18')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="apply lunch time 12–14 to all days">
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => this.applyPresetAllDays([{ start: '12:00', end: '14:00' }])}
                    >
                      {ETVConstants.trans('lunch_time_12_14')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="apply evening 18–22 to all days">
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => this.applyPresetAllDays([{ start: '18:00', end: '22:00' }])}
                    >
                      {ETVConstants.trans('evening_18_22')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="apply split shift to all days">
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        this.applyPresetAllDays([
                          { start: '09:00', end: '12:00' },
                          { start: '14:00', end: '18:00' },
                        ])
                      }
                    >
                      {ETVConstants.trans('all_days_split_shift')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="clear all days">
                    <Button type="button" variant="outlined" color="error" size="small" onClick={() => this.emit('weekly', emptyWeekly(), [])}>
                      {ETVConstants.trans('clear_all')}
                    </Button>
                  </Tooltip>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </LocalizationProvider>
    );
  }
}
